import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableRow,
  TableCell,
  Box,
} from "@mui/material";
import { ExpandMore, Photo } from "@mui/icons-material";
import PATAddButton from "../Buttons/PATAddButton";
import PATEditButton from "../Buttons/PATEditButton";
import PATDeleteButton from "../Buttons/PATDeleteButton";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import {
  createPhoto,
  deletePhoto,
  getPhotosByEntityId,
  updatePhoto,
} from "../../reducers/PersonnelPhotosReducer";
import PhotoDialog from "./PhotoDialog";
import CustomTable from "../CustomTable";
import { PerPhotoType } from "../../utils/enums";
import { getAccessPermission } from "../../reducers/PermissionsReducer";

const tableHeaders = ["Preview", "File Name", "Thumbnail", "Notes", "Actions"];

const PhotosTable = ({
  id,
  personnelPhotos,
  theme,
  allExpanded = false,
  photoFor = PerPhotoType.Employee,
  readOnly,
}) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);
  const [openPhotoDialog, setOpenPhotoDialog] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [expanded, setExpanded] = useState(allExpanded);

  const getPhotoTabPermissions = () => {
    if (photoFor === PerPhotoType.Applicant) {
      return getAccessPermission("applicants", "Photo Tab", "any");
    }
    if (photoFor === PerPhotoType.Employee) {
      return getAccessPermission("employees", "Photo Tab", "any");
    }
    return null;
  };

  const photoTabPermission = getPhotoTabPermissions();
  const canAdd = photoTabPermission?.Create ?? true;
  const canEdit = photoTabPermission?.Edit ?? true;
  const canDelete = photoTabPermission?.Delete ?? true;

  useEffect(() => {
    setExpanded(allExpanded);
  }, [allExpanded]);

  useEffect(() => {
    if (id) {
      dispatch(getPhotosByEntityId(id, photoFor));
    }
  }, [id, dispatch]);

  const processedImages = (personnelPhotos || []).map((photo) => {
    let imageSrc = null;

    if (photo.imageData) {
      try {
        const base64 =
          typeof photo.imageData === "string"
            ? photo.imageData
            : btoa(
                new Uint8Array(photo.imageData.data || photo.imageData).reduce(
                  (data, byte) => data + String.fromCharCode(byte),
                  ""
                )
              );

        imageSrc = `data:image/jpeg;base64,${base64}`;
      } catch (err) {
        console.error("Error converting image data:", err);
        imageSrc = null;
      }
    }

    return {
      ...photo,
      imageSrc,
    };
  });

  const handleOpenAddPhoto = (e) => {
    e.stopPropagation();
    setEditingPhoto(null);
    setOpenPhotoDialog(true);
  };

  const handleOpenEditPhoto = (e, photo) => {
    e.stopPropagation();
    setEditingPhoto(photo);
    setOpenPhotoDialog(true);
  };

  const handleSavePhoto = async (data) => {
    const isEditing = !!editingPhoto;
    const currentId = editingPhoto?.imageID;

    // If new thumbnail is being set, unset previous one
    if (data.isThumbnail) {
      const existingThumbnail = personnelPhotos.find(
        (p) => p.isThumbnail && p.imageID !== currentId
      );

      if (existingThumbnail) {
        const cleared = {
          ...existingThumbnail,
          isThumbnail: false,
          imageData: existingThumbnail.imageData,
        };

        await dispatch(
          updatePhoto(existingThumbnail.imageID, id, photoFor, cleared)
        );
      }
    }

    if (isEditing) {
      const updatedData = {
        ...data,
        imageData: data.imageData ?? editingPhoto.imageData,
      };

      dispatch(
        updatePhoto(editingPhoto.imageID, id, photoFor, updatedData)
      ).then(() => dispatch(getPhotosByEntityId(id, photoFor)));
    } else {
      dispatch(createPhoto({ ...data, entityId: id, photoFor })).then(() =>
        dispatch(getPhotosByEntityId(id, photoFor))
      );
    }
  };

  const handleOpenDeleteDialog = (e, imageID) => {
    e.stopPropagation();
    setPhotoToDelete(imageID);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (photoToDelete) {
      dispatch(deletePhoto(photoToDelete, userData?.user?.ptsUserID, id)).then(
        () => dispatch(getPhotosByEntityId(id, photoFor))
      );
    }
    setOpenDeleteDialog(false);
    setPhotoToDelete(null);
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        sx={{ mb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: theme.accordion.backgroundColor,
            color: theme.accordion.textColor,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" alignItems="center">
              <Photo sx={{ mr: 1 }} />
              <span>Photos</span>
            </Box>
            <Box sx={{ mr: 2 }}>
              {!readOnly && canAdd && (
                <PATAddButton
                  tooltipText="Add Photo"
                  onClick={handleOpenAddPhoto}
                />
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {processedImages && processedImages.length > 0 ? (
            <CustomTable
              columns={tableHeaders}
              data={processedImages}
              renderRow={(photo, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {photo.imageSrc ? (
                      <img
                        src={photo.imageSrc}
                        alt={photo.fileName}
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "contain",
                          borderRadius: 4,
                        }}
                      />
                    ) : (
                      "No Image"
                    )}
                  </TableCell>
                  <TableCell>{photo.fileName}</TableCell>
                  <TableCell>{photo.isThumbnail ? "Yes" : "No"}</TableCell>
                  <TableCell>{photo.notes || "—"}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {!readOnly && canEdit && (
                        <PATEditButton
                          tooltipText="Edit Photo"
                          onClick={(e) => handleOpenEditPhoto(e, photo)}
                          size="20px"
                        />
                      )}
                      {!readOnly && canDelete && (
                        <PATDeleteButton
                          tooltipText="Delete Photo"
                          onClick={(e) =>
                            handleOpenDeleteDialog(e, photo.imageID)
                          }
                          size="20px"
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            />
          ) : (
            <span>No Photos Available</span>
          )}
        </AccordionDetails>
      </Accordion>
      <PhotoDialog
        open={openPhotoDialog}
        onClose={() => setOpenPhotoDialog(false)}
        onSave={handleSavePhoto}
        initialData={editingPhoto}
        allPhotos={personnelPhotos}
      />
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setPhotoToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Photo"
        message="Are you sure you want to delete this photo?"
      />
    </>
  );
};

export default PhotosTable;
